/**
 * Statsig telemetry provider for Danny web app.
 *
 * Wraps children with the Statsig React SDK when a client key is available.
 * When no key is provided (local dev without Statsig), children render unwrapped.
 *
 * IMPORTANT: No email or PII is sent to Statsig — only opaque userId +
 * non-PII custom attributes (subscriptionTier, accountCreatedAt).
 */

import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import * as Sentry from '@sentry/react';
import {
  StatsigProvider as SdkStatsigProvider,
  useClientAsyncInit,
} from '@statsig/react-bindings';
import type { StatsigUser } from '@statsig/js-client';
import { useAuth } from '../contexts/AuthContext';

export interface StatsigBootstrap {
  gates: Record<string, boolean>;
  experiments: Record<string, Record<string, unknown>>;
  generatedAt: string;
}

interface TelemetryProviderProps {
  children: ReactNode;
  clientKey: string;
  bootstrap: StatsigBootstrap | null;
}

/**
 * Inner component that initialises the Statsig client and renders the SDK provider.
 * Separated so that hook calls (useAuth, useClientAsyncInit) are always unconditional.
 */
function StatsigInner({
  children,
  clientKey,
  bootstrap,
}: TelemetryProviderProps) {
  const { user } = useAuth();
  const sentryEnriched = useRef(false);

  // No email — only opaque userId + non-PII attributes
  const statsigUser = useMemo<StatsigUser>(
    () => ({
      userID: user?.id,
      custom: {
        subscriptionTier: user?.subscriptionTier ?? 'free',
        accountCreatedAt: user?.createdAt ?? '',
      },
    }),
    [user?.id, user?.subscriptionTier, user?.createdAt],
  );

  // Set Sentry context once so error reports include feature flag state.
  useEffect(() => {
    if (bootstrap && !sentryEnriched.current) {
      Sentry.setContext('statsig', {
        gates: bootstrap.gates,
        experiments: bootstrap.experiments,
      });
      sentryEnriched.current = true;
    }
  }, [bootstrap]);

  const { client } = useClientAsyncInit(clientKey, statsigUser);

  return (
    <SdkStatsigProvider client={client}>
      {children}
    </SdkStatsigProvider>
  );
}

/**
 * Top-level telemetry provider. Renders children immediately (non-blocking).
 * When `clientKey` is falsy, Statsig is bypassed entirely.
 */
export function TelemetryProvider({
  children,
  clientKey,
  bootstrap,
}: TelemetryProviderProps) {
  if (!clientKey) {
    return <>{children}</>;
  }

  return (
    <StatsigInner clientKey={clientKey} bootstrap={bootstrap}>
      {children}
    </StatsigInner>
  );
}
